import { useCallback } from 'react';
import { useProductTagging } from '../../context';
import type { TagDataUseToCreateTag } from '@modules/product-tagging/type';
import TaggingModalForm from '@modules/product-tagging/components/tagging-modal-form/TaggingModalForm';
import type { TaggingFormFieldDescriptor } from '@modules/product-tagging/components/tagging-modal-form/TaggingModalForm';

const FIELDS: TaggingFormFieldDescriptor[] = [
    {
        name: 'productName',
        placeholder: 'Product name',
        maxLength: 255,
        autoFocus: true,
    },
    {
        name: 'productURL',
        placeholder: 'https://supplier.com/product-page',
        maxLength: 255,
        isURL: true,
    },
];

interface ProductDetailsFormProps {
    /** The supplier selected in the previous step. */
    supplier: { id: number; title: string; link: string };
    onSubmit: (data: TagDataUseToCreateTag) => void;
    /** Called when the user dismisses the modal without submitting. */
    onDismiss?: () => void;
}

/**
 * Static-header modal collecting product name and URL when a supplier is on
 * ArchiPro but the product is not listed. Shown after the user selects a
 * supplier from the search results.
 */
const ProductDetailsForm = ({
    supplier,
    onSubmit,
    onDismiss: onDismissProp,
}: ProductDetailsFormProps) => {
    const { dispatch } = useProductTagging();

    const dismiss = useCallback(() => {
        dispatch({ type: 'ADDING_TAG', payload: undefined });
        onDismissProp?.();
    }, [dispatch, onDismissProp]);

    const buildPayload = useCallback(
        (values: Record<string, string>): TagDataUseToCreateTag => ({
            professionalForUnknownProduct: {
                id: supplier.id,
                title: supplier.title,
                link: supplier.link,
            },
            titleForUnknownProduct: values.productName,
            urlForUnknownProduct: values.productURL,
        }),
        [supplier],
    );

    return (
        <TaggingModalForm
            title="Add product details"
            message={
                <>
                    This product isn&apos;t listed on ArchiPro yet. Add its name
                    and a link to the product page so we can notify the
                    supplier.
                </>
            }
            fields={FIELDS}
            buildPayload={buildPayload}
            onSubmit={onSubmit}
            onDismiss={dismiss}
            formId="product-details-form"
        />
    );
};

export default ProductDetailsForm;
